<template>
  <v-container fluid class="pa-2">

    <v-flex xs12 class="my-0">
      <v-img
        :src="require('@/assets/TastingIQlogotype-withHSP.svg')"
        contain
        max-width="300"
        class="mb-1 my-0 mx-auto"
      />
    </v-flex>

    <v-layout row wrap align-center class="mb-2">
      <v-flex xs12 sm8>
        <div class="shared-title">{{ retailName || 'Event Calendar' }}</div>
        <div class="shared-subtitle" v-if="dateStart && dateEnd">
          {{ formatDate(dateStart) }} - {{ formatDate(dateEnd) }}
        </div>
        <br>
        <div class="shared-subtitle">
          All tasting event requests are subject to ABSCO approval.
        </div>
      </v-flex>
      <v-flex xs12 sm4 class="text-xs-right">
        <v-progress-circular v-if="loading" indeterminate color="primary" class="mr-3" />
        <v-btn v-if="!loading && !error" flat @click="exportCSV">
          <v-icon left color="dark_grey">mdi-cloud-download</v-icon> Download CSV
        </v-btn>
      </v-flex>
    </v-layout>

    <v-alert type="error" :value="!!error">
      {{ error }}
    </v-alert>

    <div v-if="!error" class="table-scroll-container">
      <table class="store-table">

        <thead>
          <tr>
            <th class="col-store col-corner">
              <div class="store-header-labels">
                <span>Store</span>
              </div>
            </th>
            <th
              v-for="day in calendarDays"
              :key="day.dateStr"
              class="col-day"
              :class="{ 'week-start': day.weekStart }"
            >
              <div class="day-number">{{ day.day }}</div>
              <div class="day-dow">{{ dowLabels[day.dow] }}</div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="store in stores" :key="store.costco_id">
            <td class="col-store store-info-cell">
              <span class="store-cluster">{{ store.cluster || '-' }}</span>
              <span class="store-whid">#{{ store.warehouse_id }}</span>
              <span class="store-city">{{ store.city }}</span>
            </td>
            <td
              v-for="day in calendarDays"
              :key="day.dateStr"
              class="col-day day-cell"
              :class="{
                'week-start': day.weekStart,
                'empty-cell': !isBusy(store.costco_id, day.dateStr)
              }"
            >
              <div
                v-if="isBusy(store.costco_id, day.dateStr)"
                class="busy-pill"
                :class="{ 'week-alt': day.weekAlt }"
              >
                Busy
              </div>
            </td>
          </tr>
        </tbody>

      </table>
    </div>

    <v-flex xs12 class="text-xs-center mt-4">
      <p class="grey--text">
        This is a shared availability view. Open dates are available to schedule.
        Email <a href="mailto:tastings@hspbrands.com">tastings@hspbrands.com</a> with questions.
      </p>
    </v-flex>

  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  data: () => ({
    loading: true,
    error: null,
    retailName: '',
    dateStart: '',
    dateEnd: '',
    stores: [],
    busy: {},
    dowLabels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  }),

  computed: {
    calendarDays () {
      if (!this.dateStart || !this.dateEnd) return []
      const days = []
      // Treat API dates as calendar dates, independent of browser DST.
      const start = new Date(this.dateStart + 'T00:00:00Z')
      const end = new Date(this.dateEnd + 'T00:00:00Z')
      let weekIndex = 0
      for (const d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const dow = d.getUTCDay()
        const weekStart = dow === 1 && days.length > 0
        if (weekStart) weekIndex++
        days.push({
          day: d.toLocaleString('default', { month: 'short', timeZone: 'UTC' }) + ' ' + d.getUTCDate(),
          dateStr: d.toISOString().slice(0, 10),
          dow,
          weekStart,
          weekAlt: weekIndex % 2 === 1
        })
      }
      return days
    },

    busyMap () {
      const map = {}
      for (const [date, storeIds] of Object.entries(this.busy)) {
        for (const id of storeIds) {
          if (!map[id]) map[id] = {}
          map[id][date] = true
        }
      }
      return map
    },
  },

  created () {
    this.fetchSharedCalendar()
  },

  methods: {
    formatDate (date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },

    isBusy (costcoId, dateStr) {
      return !!(this.busyMap[costcoId] && this.busyMap[costcoId][dateStr])
    },

    async fetchSharedCalendar () {
      this.loading = true
      this.error = null
      try {
        // Public view: resolve the API root directly, no login bootstrap
        const config = await axios.get('/config/config.json')
        const apiRoot = config.data.api_root

        const response = await axios.get(
          `${apiRoot}retail/${this.$route.params.retail_code}/shared_calendar`
        )
        this.retailName = response.data.retail_name || ''
        this.dateStart = response.data.date_start || ''
        this.dateEnd = response.data.date_end || ''
        this.stores = response.data.stores || []
        this.busy = response.data.busy || {}
      } catch (err) {
        console.error('Failed to fetch shared calendar:', err)
        this.error = 'This calendar link is invalid. Please check the URL or contact the sender.'
      } finally {
        this.loading = false
      }
    },

    downloadCSV (filename, headers, rows) {
      const escape = v => {
        const s = v == null ? '' : String(v)
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const lines = [headers, ...rows].map(r => r.map(escape).join(','))
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
    },

    exportCSV () {
      const days = this.calendarDays
      const headers = [
        'Cluster', 'Store #', 'City',
        ...days.map(day => this.formatDate(day.dateStr))
      ]
      const rows = this.stores.map(store => [
        store.cluster || '',
        store.warehouse_id,
        store.city,
        ...days.map(day => this.isBusy(store.costco_id, day.dateStr) ? 'Busy' : '')
      ])
      this.downloadCSV(`availability_${this.dateStart}_${this.dateEnd}.csv`, headers, rows)
    },
  }
}
</script>

<style scoped>
.shared-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.shared-subtitle {
  font-size: 13px;
  color: #888;
}

.table-scroll-container {
  overflow: auto;
  max-height: calc(100vh - 200px);
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.store-table {
  border-collapse: collapse;
  width: 100%;
  min-width: max-content;
  font-size: 13px;
}

thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f5f5f5;
  border-bottom: 2px solid #d0d0d0;
  white-space: nowrap;
  padding: 6px 8px;
  text-align: center;
}

.col-store {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  border-right: 2px solid #d0d0d0;
  max-width: 180px;
  min-width: 180px;
}

.col-corner {
  z-index: 3;
  background: #f5f5f5;
}

.col-day {
  min-width: 100px;
}

.week-start {
  border-left: 3px solid #b0b0b0 !important;
}

.day-number {
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.day-dow {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.store-info-cell {
  padding: 4px 8px;
  border-bottom: 1px solid #ececec;
}

.store-header-labels {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
}

.store-cluster { color: #888; font-size: 12px; margin-right: 4px; }
.store-whid    { font-weight: 700; font-size: 13px; margin-right: 4px; }
.store-city    { color: #444; font-size: 13px; }

.day-cell {
  padding: 3px 4px;
  vertical-align: top;
  border-bottom: 1px solid #ececec;
  border-left: 1px solid #ececec;
}

.empty-cell { background: #f7f8fa; }

.busy-pill {
  background: #1565C0;
  color: #fff;
  border-radius: 4px;
  padding: 4px 7px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.busy-pill.week-alt { background: #6200ea; }
</style>
